import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { NoIntegrationDialog } from 'src/shared/dialogs';
import { EcommerceId } from 'src/shared/enums';
import { getPathName } from 'src/shared/helpers';
import { useDialogManager } from '../dialog';
import { helpersMenuQuery } from './__generated__/helpersMenuQuery.graphql';
import { PageBlock } from './pageBlocksList';

// From one day, on prod it stopped working the imported T from helpers.
class T {
    public static _(message: string, domain: string): string {
        const wnd = window as any;
        return wnd.T2 ? wnd.T2._(message, domain) : message;
    }
}

const findMenuItemByUrlPage = (items: any[], pageName: string): any | undefined => {
    const normalizedPageName = pageName.toLowerCase();

    for (const item of items) {
        if (item.urlPage?.toLowerCase().endsWith(normalizedPageName)) {
            return item;
        }

        const childMatch = findMenuItemByUrlPage(item.menu ?? [], pageName);
        if (childMatch) {
            return childMatch;
        }
    }

    return undefined;
};

export const findMenuItemByKey = (items: any[], key: string): any | undefined => {
    for (const item of items) {
        if (item.key === key) {
            return item;
        }

        const childMatch = findMenuItemByKey(item.menu ?? [], key);
        if (childMatch) {
            return childMatch;
        }
    }

    return undefined;
};

export const useTreeMenu = (lastUsedTool?: number) => {
    const query = useLazyLoadQuery<helpersMenuQuery>(
        graphql`
            query helpersMenuQuery($lastUsedTool: Int) {
                treeMenu(lastUsedTool: $lastUsedTool)
            }
        `,
        { lastUsedTool: lastUsedTool ?? 1 }
    );

    return JSON.parse(query.treeMenu);
};

const mapMenuItemsToPageBlocks = (
    items: any[],
    ecommerceName: EcommerceId | undefined,
    showDialog: ReturnType<typeof useDialogManager>['showDialog']
): PageBlock[] =>
    items.map((x: any) => {
        if (x.regex === '(get-a-quote)' && ecommerceName === EcommerceId.NONE)
            return {
                label: x.name,
                isNew: x.isNew,
                description: x.description,
                permissions: x.permissions,
                learnMore: x.learnMore,
                buttonLabel: T._('Manage', 'Admin'),
                buttonAction: () => {
                    showDialog(
                        'complete-ecommerce-integration',
                        <NoIntegrationDialog
                            subTitleNoIntegration={T._('Ready to allow your customers to send you requests for quotations?', 'Admin')}
                            message={
                                <label>
                                    {T._(
                                        'In order to activate the Get a Quote tool you need to complete the Zakeke integration by connecting it with your store',
                                        'Admin'
                                    )}
                                </label>
                            }
                        ></NoIntegrationDialog>
                    );
                }
            } as PageBlock;
        else if (x.icon === 'sliders_h_solid' && ecommerceName === EcommerceId.NONE)
            return {
                label: x.name,
                isNew: x.isNew,
                description: x.description,
                permissions: x.permissions,
                learnMore: x.learnMore,
                buttonLabel: T._('Manage', 'Admin'),
                buttonAction: () => {
                    showDialog(
                        'complete-ecommerce-integration',
                        <NoIntegrationDialog
                            subTitleNoIntegration={T._('Do you want to integrate your third-party catalog?', 'Admin')}
                            message={
                                <div>
                                    <label>
                                        {T._(
                                            'In order to sell customizable products from third-party catalogs, you need to connect your store first. Please note that this option is only valid if your store is built on one of the following platforms:',
                                            'Admin'
                                        )}
                                    </label>
                                    <ul>
                                        <li>Shopify</li>
                                        <li>WooCommerce</li>
                                        <li>BigCommerce</li>
                                    </ul>
                                    <a
                                        href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product#h_01GH9KKJV4QC09RBBFRBVDJPEN'
                                        target='_blank'
                                    >
                                        {T._('Read more here.', 'Admin')}
                                    </a>
                                </div>
                            }
                        ></NoIntegrationDialog>
                    );
                }
            };
        else {
            var urlPage = getPathName(x.urlPage);
            if (x.name === 'Order desk') {
                urlPage = x.urlPage;
            }
            return {
                label: x.name,
                isNew: x.isNew,
                isComingSoon: x.isComingSoon,
                isEarlyAccess: x.isEarlyAccess,
                description: x.description,
                permissions: x.permissions,
                learnMore: x.learnMore,
                buttonLabel: T._('Manage', 'Admin'),
                link: urlPage
            };
        }
    });

export const usePageBlocksListFromMenuKey = (menuKey: string | undefined, ecommerceName?: EcommerceId, lastUsedTool?: number) => {
    const { showDialog } = useDialogManager();
    const treeMenu = useTreeMenu(lastUsedTool);
    const menuItem = menuKey ? findMenuItemByKey(treeMenu.menu ?? [], menuKey) : undefined;

    return {
        title: menuItem?.name ?? '',
        pageKey: menuItem?.key,
        blocks: mapMenuItemsToPageBlocks(menuItem?.menu ?? [], ecommerceName, showDialog)
    };
};

export default function usePageBlocksListFromJson(pageName: string, ecommerceName?: EcommerceId, lastUsedTool?: number): PageBlock[] {
    const { showDialog } = useDialogManager();
    const treeMenu = useTreeMenu(lastUsedTool);
    const tools = findMenuItemByUrlPage(treeMenu.menu ?? [], pageName)?.menu ?? [];

    return mapMenuItemsToPageBlocks(tools, ecommerceName, showDialog);
}
